/**
 * Course Progress Bar
 *
 * Filled progress bar with the percentage rendered inside the bar itself.
 * Used for course/learning-path progress on cards and detail views.
 */

import { Box, Typography } from '../ui';

interface CourseProgressBarProps {
  /** Progress value 0–100. Out-of-range values are clamped. */
  value: number;
  /** Optional prefix shown alongside the percentage (e.g. "Completed"). */
  label?: string;
  /** Visual variant — "success" turns the fill green for completed states. */
  variant?: 'primary' | 'success';
  /** Bar height in pixels (default 22). */
  height?: number;
  sx?: any;
}

export default function CourseProgressBar({ value, label, variant = 'primary', height = 22, sx }: CourseProgressBarProps) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const text = label ? `${label} — ${pct}%` : `${pct}%`;
  const fillColor = variant === 'success' ? 'success.main' : 'primary.main';

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        bgcolor: 'grey.200',
        borderRadius: 1,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: `${pct}%`,
          bgcolor: fillColor,
          transition: 'width 0.3s ease',
        }}
      />
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.75rem',
          color: 'white',
          // Shadow keeps the label legible over both the filled and unfilled
          // halves of the bar (white-on-grey would otherwise wash out).
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.65)',
          pointerEvents: 'none',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
